/* eslint-disable max-lines-per-function, complexity, max-len, @typescript-eslint/no-explicit-any */
import { useState, useEffect, useCallback, useRef } from 'react';
import { getProdSupabaseClient } from '@/lib/supabase/client';
import { transformCarsWithSafetyRecallsToAppFormat } from '@/lib/supabase/transform';
import { RecallVehicle } from '../services/recallAPI';
import { getRecallsForTenant, getProdClientWithSession } from '@/lib/supabase/tenantScoping';
import {
  setCache,
  getCache,
  setStructuredCache,
  getStructuredCache,
  removeCache,
  initializeCacheCleanup
} from '@/lib/utils/cache';
import { pickLatestPerCampaignField, type UserPrefRow } from '@/lib/supabase/campaigns';
import {
  decideRawCacheWrite,
  isLastPage,
  isStoredDatasetComplete,
  loadAllPages,
  SERVER_MAX_ROWS,
  type RawRecallsMeta
} from '../utils/fullDatasetCache';

interface MapBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

interface UseViewportMapDataReturn {
  recalls: RecallVehicle[];
  allRecallsCache: RecallVehicle[] | null;
  loading: boolean;
  error: string | null;
  loadViewportData: (bounds: MapBounds) => Promise<void>;
  totalCount: number;
  isLoadingMore: boolean;
  hasMore: boolean;
  preferencesLoaded: boolean;
  isCacheBuilding: boolean;
  hasCachedData: boolean;
  isBackgroundLoading: boolean;
  /** Set when the full dataset failed to load; whatever was already shown stays. */
  fullLoadError: string | null;
  /** Load the full dataset from the server again. */
  refetchFullDataset: () => void;
}

// Requested rows per page. The server may cap a page lower (see SERVER_MAX_ROWS),
// so pages advance by the rows actually received.
const PAGE_SIZE = 10000;
const MAX_PAGE_BUDGET = 3;
const SMALL_VIEWPORT_AREA = 0.25; // Roughly ~0.5 deg lat * 0.5 deg lng
const MEDIUM_VIEWPORT_AREA = 1.5;
const FULL_DATA_MAX_PAGES = 20;
// Full-dataset pages requested at once after the first.
const FULL_DATA_CONCURRENCY = 4;

// Cache keys. The raw dataset is kept in IndexedDB as objects (setStructuredCache):
// LZ-compressing ~20MB on every visit froze the page for over 3 seconds.
const CACHE_KEY_RAW_RECALLS = 'raw_recalls_v2';
const CACHE_KEY_RAW_RECALLS_META = 'raw_recalls_v2_meta';
const LEGACY_RAW_RECALLS_KEYS = ['raw_recalls', 'raw_recalls_meta'];
const CACHE_KEY_PREFERENCES = 'user_preferences';
const RAW_RECALLS_TTL = 2 * 60 * 60 * 1000; // 2 hours (MV refreshes every few days)

// Debug logging flag (can be controlled via environment variable)
const DEBUG_CACHE = import.meta.env.DEV || false;

const getViewportArea = (bounds: MapBounds) => {
  const latSpan = Math.abs(bounds.north - bounds.south);
  const lngSpan = Math.abs(bounds.east - bounds.west);
  return latSpan * lngSpan;
};

const determinePageBudget = (bounds: MapBounds) => {
  const area = getViewportArea(bounds);
  if (area <= SMALL_VIEWPORT_AREA) {
    return 1;
  }
  if (area <= MEDIUM_VIEWPORT_AREA) {
    return Math.min(2, MAX_PAGE_BUDGET);
  }
  return MAX_PAGE_BUDGET;
};

const formatViewportError = (err: unknown) => {
  if (err instanceof Error) {
    if (/statement timeout/i.test(err.message) || /canceling statement/i.test(err.message)) {
      return 'Viewport query timed out. Try zooming in or narrowing the filters.';
    }
    return err.message;
  }
  return 'Failed to load viewport data';
};

/**
 * Deduplicate recalls by VIN + campaign_number, keeping the record with the latest snapshot_date.
 * This prevents the same recall from appearing multiple times when a vehicle appears in
 * multiple zones' latest snapshots (e.g., vehicle moved between dealers in different zones).
 */
const deduplicateByVinCampaign = (recalls: RecallVehicle[]): RecallVehicle[] => {
  const recallMap = new Map<string, RecallVehicle>();
  recalls.forEach(recall => {
    const key = `${recall.vin}-${recall.campaign_number}`;
    const existing = recallMap.get(key);
    if (!existing) {
      recallMap.set(key, recall);
    } else {
      // Keep the one with latest snapshot_date
      const existingDate = (existing as any).snapshot_date;
      const newDate = (recall as any).snapshot_date;
      if (newDate && (!existingDate || newDate > existingDate)) {
        recallMap.set(key, recall);
      }
    }
  });
  return Array.from(recallMap.values());
};

// Filter recalls by viewport bounds - handles both raw (Record) and transformed data
const filterRecallsByBounds = (
  data: RecallVehicle[] | Record<string, unknown>[],
  bounds: MapBounds
): RecallVehicle[] | Record<string, unknown>[] => {
  const rows = data as Array<RecallVehicle | Record<string, unknown>>;
  return rows.filter((recall: RecallVehicle | Record<string, unknown>) => {
    // Handle both transformed (RecallVehicle) and raw (Record) data
    const latitude =
      (recall as RecallVehicle).dealer_latitude ??
      (recall as RecallVehicle).coordinates?.latitude ??
      ((recall as Record<string, unknown>)['dealer_latitude'] as number | null) ??
      ((recall as Record<string, unknown>)['seller_latitude'] as number | null) ??
      null;

    const longitude =
      (recall as RecallVehicle).dealer_longitude ??
      (recall as RecallVehicle).coordinates?.longitude ??
      ((recall as Record<string, unknown>)['dealer_longitude'] as number | null) ??
      ((recall as Record<string, unknown>)['seller_longitude'] as number | null) ??
      null;

    if (latitude === null || longitude === null) {
      return false;
    }

    return (
      latitude >= bounds.south &&
      latitude <= bounds.north &&
      longitude >= bounds.west &&
      longitude <= bounds.east
    );
  }) as RecallVehicle[] | Record<string, unknown>[];
};

/**
 * useViewportMapDataPaginated - Viewport-based recall data with localStorage caching
 *
 * CACHING STRATEGY:
 * - Loads cached data from localStorage immediately (instant UX)
 * - Refreshes data from database in background (keeps cache fresh)
 * - Reduces database reads by ~90% for repeat visits
 * - Tenant-isolated cache keys ensure security
 * - Automatic cleanup of expired entries
 * - Compression reduces storage usage
 *
 * CACHE KEYS:
 * - raw_recalls_v2: Full tenant recall dataset, IndexedDB objects (2h TTL)
 * - user_preferences: User preferences (10min TTL)
 *
 * DEBUG LOGGING:
 * - Enable via DEBUG_CACHE flag (auto-enabled in dev mode)
 * - Logs cache hits/misses, DB read counts, compression ratios
 */
export const useViewportMapDataPaginated = (
  userId?: string,
  tenantId?: string | null
): UseViewportMapDataReturn => {
  const [recalls, setRecalls] = useState<RecallVehicle[]>([]);
  const [loading, setLoading] = useState(false);
  const hasRecallsRef = useRef(false); // Track if we've ever had recalls to prevent clearing
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [excludedDealers, setExcludedDealers] = useState<string[]>([]);
  const [completedRecalls, setCompletedRecalls] = useState<Set<string>>(new Set());
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);
  const [campaignPreferences, setCampaignPreferences] = useState<
    Record<string, { mobile_eligible?: boolean; skill_level?: number }>
  >({});
  const [allRecallsCache, setAllRecallsCache] = useState<RecallVehicle[] | null>(null);
  const [isCacheBuilding, setIsCacheBuilding] = useState(false);
  const viewportLoadInProgressRef = useRef(false);
  const pendingBoundsRef = useRef<MapBounds | null>(null);
  const cachedRawRecallsRef = useRef<Record<string, unknown>[] | null>(null);
  const baseTransformedCacheRef = useRef<RecallVehicle[] | null>(null);
  const lastRequestedBoundsRef = useRef<MapBounds | null>(null);
  const fullDataCompleteRef = useRef(false);
  const cachedTenantRef = useRef<string | null>(null);
  const cacheInitializedRef = useRef(false);
  const applyCacheForBoundsRef = useRef<((bounds: MapBounds) => boolean) | null>(null);
  const [hasCachedData, setHasCachedData] = useState(false);
  const [isBackgroundLoading, setIsBackgroundLoading] = useState(false);
  const [fullLoadError, setFullLoadError] = useState<string | null>(null);
  // Bumped to load the full dataset again (retry after a failure).
  const [fullLoadNonce, setFullLoadNonce] = useState(0);
  const rebuildCacheFromRawRef = useRef<((rawRows?: Record<string, unknown>[]) => void) | null>(
    null
  );
  // The full dataset loads without waiting for preferences, but is only shown once they
  // are known (exclusions and completions must never flash on the map).
  const preferencesLoadedRef = useRef(false);
  const paintPendingRef = useRef(false);
  // While the full dataset loads, viewport requests wait for it instead of querying alone.
  const fullLoadInFlightRef = useRef(false);

  const applyClientFilters = useCallback(
    (data: RecallVehicle[]) => {
      let filtered = data;

      if (excludedDealers.length > 0) {
        filtered = filtered.filter(recall => {
          const dealerName = recall.seller_name || '';
          return !excludedDealers.includes(dealerName);
        });
      }

      if (completedRecalls.size > 0) {
        filtered = filtered.filter(recall => {
          // Each transformed recall entry has its own campaign_number directly
          // Check if this specific VIN + campaign combination is completed
          const recallKey = `${recall.vin}-${recall.campaign_number}`;
          return !completedRecalls.has(recallKey);
        });
      }

      return filtered;
    },
    [completedRecalls, excludedDealers]
  );

  const rebuildCacheFromRaw = useCallback(
    (rawRows?: Record<string, unknown>[]) => {
      const sourceRows = rawRows ?? cachedRawRecallsRef.current;
      if (!sourceRows || sourceRows.length === 0) {
        return;
      }

      const transformedBase = transformCarsWithSafetyRecallsToAppFormat(
        sourceRows,
        campaignPreferences
      );
      // Deduplicate by VIN+campaign to prevent cross-zone duplicates
      const deduplicatedBase = deduplicateByVinCampaign(transformedBase);
      baseTransformedCacheRef.current = deduplicatedBase;
      const filtered = applyClientFilters(deduplicatedBase);
      setAllRecallsCache(filtered);
    },
    [applyClientFilters, campaignPreferences]
  );

  // The full-dataset load reads the latest rebuild through this ref, so preference
  // updates never restart (and discard) a load that is in flight.
  useEffect(() => {
    rebuildCacheFromRawRef.current = rebuildCacheFromRaw;
  }, [rebuildCacheFromRaw]);

  // Campaign preferences change how raw rows are transformed; re-transform what is held.
  // (Exclusion/completion changes only re-filter, in the effect below.)
  useEffect(() => {
    if (cachedRawRecallsRef.current && preferencesLoadedRef.current) {
      rebuildCacheFromRawRef.current?.();
    }
  }, [campaignPreferences]);

  // Show rows that arrived before preferences did.
  useEffect(() => {
    preferencesLoadedRef.current = preferencesLoaded;
    if (preferencesLoaded && paintPendingRef.current) {
      paintPendingRef.current = false;
      rebuildCacheFromRawRef.current?.();
      if (lastRequestedBoundsRef.current && applyCacheForBoundsRef.current) {
        applyCacheForBoundsRef.current(lastRequestedBoundsRef.current);
      }
    }
  }, [preferencesLoaded]);

  useEffect(() => {
    if (baseTransformedCacheRef.current) {
      const filtered = applyClientFilters(baseTransformedCacheRef.current);

      // Only update if we have data (prevent clearing cache with empty results)
      if (filtered.length > 0) {
        setAllRecallsCache(filtered);

        // If we have a pending viewport request, reapply it with updated filters
        // This happens when preferences update and filters change
        if (lastRequestedBoundsRef.current) {
          if (DEBUG_CACHE) {
            console.log(
              `[useViewportMapDataPaginated] Reapplying viewport with updated filters (${filtered.length} recalls)`
            );
          }
          // Apply immediately - don't use setTimeout to prevent flicker
          const bounds = lastRequestedBoundsRef.current;
          const filteredByBounds = filterRecallsByBounds(filtered, bounds) as RecallVehicle[];

          // Update recalls state immediately to prevent flicker
          if (filteredByBounds.length > 0) {
            hasRecallsRef.current = true;
          }
          setRecalls(filteredByBounds);
          setHasMore(!fullDataCompleteRef.current);
          setError(null);
          setLoading(false); // Ensure loading is false (don't show loading during filter update)
          setIsLoadingMore(false);
        }
      } else if (DEBUG_CACHE) {
        console.warn(
          '[useViewportMapDataPaginated] Filtered cache is empty, keeping existing cache'
        );
      }
    }
  }, [applyClientFilters]);

  // OPTIMIZATION: Load all preferences in parallel with localStorage caching
  useEffect(() => {
    const loadPreferences = async () => {
      if (!userId || !tenantId) {
        setPreferencesLoaded(true);
        return;
      }

      try {
        // Try to load from cache first for instant UX
        const cachedPrefs = await getCache<{
          excludedDealers: string[];
          campaignPreferences: Record<string, { mobile_eligible?: boolean; skill_level?: number }>;
          completedRecalls: string[];
        }>(CACHE_KEY_PREFERENCES, {
          tenantId,
          debug: DEBUG_CACHE
        });

        if (cachedPrefs) {
          if (DEBUG_CACHE) {
            console.log('[useViewportMapDataPaginated] Loaded preferences from localStorage cache');
          }
          setExcludedDealers(cachedPrefs.excludedDealers || []);
          setCampaignPreferences(cachedPrefs.campaignPreferences || {});
          setCompletedRecalls(new Set(cachedPrefs.completedRecalls || []));
          setPreferencesLoaded(true);

          // Continue loading fresh data in background
        } else {
          if (DEBUG_CACHE) {
            console.log(
              '[useViewportMapDataPaginated] No cached preferences, loading from database'
            );
          }
        }

        // Use prod schema client for user_preferences
        const prodClient = getProdSupabaseClient();

        // Execute all preference queries in PARALLEL
        // IMPORTANT: Filter by tenant_id to prevent cross-tenant preference leakage
        const prefStartTime = performance.now();
        // Dealer exclusions are tenant-wide: if ANY user excludes a dealer, it applies to all users
        const [dealerPrefsResult, campaignPrefsResult, completedRecallsResult] = await Promise.all([
          prodClient
            .from('user_preferences')
            .select('entity_id, preference_value')
            .eq('tenant_id', tenantId)
            .eq('preference_type', 'dealer'),
          // Tenant-wide read (no user_id filter) — Skill Level + Mobile Eligible
          // are last-writer-wins for the whole tenant. Field-level dedup happens
          // below via pickLatestPerCampaignField.
          prodClient
            .from('user_preferences')
            .select('entity_id, preference_value, user_id, updated_at')
            .eq('tenant_id', tenantId)
            .eq('preference_type', 'campaign'),
          prodClient.from('completed_recalls').select('vin, campaign_number')
        ]);
        const prefQueryTime = performance.now() - prefStartTime;

        if (DEBUG_CACHE) {
          console.log(
            `[useViewportMapDataPaginated] Preferences loaded from database (3 DB reads, ${prefQueryTime.toFixed(0)}ms)`
          );
        }

        // Process dealer preferences (tenant-wide: deduplicate by entity_id, excluded wins)
        let excluded: string[] = [];
        if (dealerPrefsResult.data && dealerPrefsResult.data.length > 0) {
          const excludedSet = new Set<string>();
          dealerPrefsResult.data.forEach((p: { entity_id: string; preference_value: unknown }) => {
            const pref = p.preference_value;
            const isExcluded =
              pref === false ||
              (typeof pref === 'object' &&
                pref !== null &&
                'is_included' in pref &&
                (pref as { is_included?: boolean }).is_included === false);
            if (isExcluded) {
              excludedSet.add(p.entity_id);
            }
          });
          excluded = Array.from(excludedSet);
          setExcludedDealers(excluded);
        }

        // Process campaign preferences with field-level last-writer-wins dedup.
        // Each field is resolved independently across the tenant — the user who
        // most recently wrote skill_level wins for skill_level, same for mobile.
        const prefsMap: Record<string, { mobile_eligible?: boolean; skill_level?: number }> = {};
        if (campaignPrefsResult.data && campaignPrefsResult.data.length > 0) {
          const rows = campaignPrefsResult.data as UserPrefRow[];
          const skillByCampaign = pickLatestPerCampaignField<number>(rows, pref => {
            const v = pref['skill_level'];
            return typeof v === 'number' ? v : undefined;
          });
          const mobileByCampaign = pickLatestPerCampaignField<boolean>(rows, pref => {
            const explicit = pref['mobile_servicing_eligible'] ?? pref['mobile_eligible'];
            return typeof explicit === 'boolean' ? explicit : undefined;
          });
          const campaignNumbers = new Set<string>([
            ...Object.keys(skillByCampaign),
            ...Object.keys(mobileByCampaign)
          ]);
          campaignNumbers.forEach(num => {
            prefsMap[num] = {
              ...(skillByCampaign[num] !== undefined && { skill_level: skillByCampaign[num] }),
              ...(mobileByCampaign[num] !== undefined && { mobile_eligible: mobileByCampaign[num] })
            };
          });
          setCampaignPreferences(prefsMap);
        }

        // Process completed recalls
        const completedSet = new Set<string>();
        if (completedRecallsResult.data && completedRecallsResult.data.length > 0) {
          (completedRecallsResult.data as Array<{ vin: string; campaign_number: string }>).forEach(
            r => {
              // Create a unique key for each VIN + Campaign combination
              completedSet.add(`${r.vin}-${r.campaign_number}`);
            }
          );
          setCompletedRecalls(completedSet);
        }

        // Cache preferences for next time
        if (tenantId) {
          await setCache(
            CACHE_KEY_PREFERENCES,
            {
              excludedDealers: excluded,
              campaignPreferences: prefsMap,
              completedRecalls: Array.from(completedSet)
            },
            {
              tenantId,
              ttl: 5 * 60 * 1000, // 5 min (exclusions change frequently)
              compress: true,
              debug: DEBUG_CACHE
            }
          );
        }

        setPreferencesLoaded(true);
      } catch (err) {
        // Silently handle errors and set loaded to true to allow map to function
        setPreferencesLoaded(true);
        if (DEBUG_CACHE) {
          console.error('[useViewportMapDataPaginated] Error loading preferences:', err);
        }
      }
    };

    loadPreferences();
  }, [userId, tenantId]);

  // Initialize cache cleanup when tenant changes
  useEffect(() => {
    const normalizedTenant = tenantId ?? null;
    if (!normalizedTenant) return;

    if (!cacheInitializedRef.current) {
      initializeCacheCleanup(normalizedTenant, DEBUG_CACHE);
      cacheInitializedRef.current = true;
    }

    if (cachedTenantRef.current === normalizedTenant) {
      return;
    }

    // Tenant changed - clear old cache references
    const oldTenant = cachedTenantRef.current;
    cachedTenantRef.current = normalizedTenant;
    cachedRawRecallsRef.current = null;
    baseTransformedCacheRef.current = null;
    setAllRecallsCache(null);
    fullDataCompleteRef.current = false;

    // Clear cache for old tenant if it exists (safety measure)
    if (oldTenant && oldTenant !== normalizedTenant) {
      if (DEBUG_CACHE) {
        console.log('[useViewportMapDataPaginated] Tenant changed, clearing cache for old tenant');
      }
      // Note: We don't clear localStorage here as it's tenant-isolated by key
      // The cache utility handles tenant isolation automatically
    }

    // Load cached raw data for new tenant immediately
    // Note: This is async and non-blocking, will be handled in buildFullDataset effect
    // We don't load here to avoid race conditions with the main cache loading effect
  }, [tenantId, preferencesLoaded, rebuildCacheFromRaw]);

  // Get total count when tenant ID is available
  // Note: count_recalls_for_tenant RPC doesn't exist, so we skip count for now
  // The count is not critical for map functionality - it's just for display
  useEffect(() => {
    // Skip count fetching - the RPC function doesn't exist
    // We could calculate count from get_recalls_for_tenant if needed, but it's not critical
    setTotalCount(0);
  }, [tenantId]);

  // Full dataset: paint the stored copy at once, then always revalidate from the server
  // (stale-while-revalidate). Only a tenant change, unmount or retry discards a load in
  // flight; preference updates reach it through rebuildCacheFromRawRef. Neither the
  // stored read nor the network load waits for preferences; painting does.
  useEffect(() => {
    if (!tenantId) {
      return;
    }

    let cancelled = false;
    fullLoadInFlightRef.current = true;
    // Set before anything awaits: the page keeps the map mounted while this is true.
    setIsCacheBuilding(true);

    // Copies from before the IndexedDB format would only waste storage.
    LEGACY_RAW_RECALLS_KEYS.forEach(key => removeCache(key, tenantId));

    // Rows are already in cachedRawRecallsRef; this only shows them.
    const paint = () => {
      if (!preferencesLoadedRef.current) {
        paintPendingRef.current = true;
        return;
      }
      rebuildCacheFromRawRef.current?.();
      // If there's a pending viewport request, apply it now
      if (lastRequestedBoundsRef.current && applyCacheForBoundsRef.current) {
        applyCacheForBoundsRef.current(lastRequestedBoundsRef.current);
      }
    };

    const buildFullDataset = async () => {
      const [cachedRaw, cachedMeta] = await Promise.all([
        getStructuredCache<Record<string, unknown>[]>(CACHE_KEY_RAW_RECALLS, {
          tenantId,
          debug: DEBUG_CACHE
        }),
        getCache<RawRecallsMeta>(CACHE_KEY_RAW_RECALLS_META, { tenantId, debug: DEBUG_CACHE })
      ]);
      if (cancelled) {
        return;
      }

      const stored = cachedRaw && cachedRaw.length > 0 ? cachedRaw : null;
      // Never replace rows already in memory with the stored copy: on a retry the
      // in-memory set is the newer server answer.
      if (stored && !cachedRawRecallsRef.current) {
        if (DEBUG_CACHE) {
          console.log(
            `[useViewportMapDataPaginated] ✅ Using ${stored.length} cached recalls from cache (0 DB reads)`
          );
        }
        cachedRawRecallsRef.current = stored;
        fullDataCompleteRef.current = isStoredDatasetComplete(stored.length, cachedMeta);
        paint();
        setHasCachedData(true);
      } else if (!stored && DEBUG_CACHE) {
        console.log('[useViewportMapDataPaginated] No cache found, loading from database');
      }

      // Revalidate in the background whenever something is already on screen.
      setIsBackgroundLoading(Boolean(cachedRawRecallsRef.current));
      try {
        const prodClient = await getProdClientWithSession();
        const buildStartTime = performance.now();

        const fetchPage = async (offset: number, limit: number) => {
          const { data, error } = await getRecallsForTenant(prodClient, tenantId, limit, offset);
          if (error) {
            throw new Error(`Failed to build recall cache: ${error.message || 'Unknown error'}`);
          }
          return (data || []) as Record<string, unknown>[];
        };

        const {
          rows: aggregatedRows,
          complete: reachedEnd,
          requests: dbReadCount
        } = await loadAllPages(fetchPage, {
          // Exactly the server cap, so every full page is the same length.
          pageSize: SERVER_MAX_ROWS,
          maxPages: FULL_DATA_MAX_PAGES,
          concurrency: FULL_DATA_CONCURRENCY,
          isCancelled: () => cancelled,
          // Nothing on screen yet: show the first page while the rest loads.
          onFirstPage: firstRows => {
            if (!cachedRawRecallsRef.current && firstRows.length > 0) {
              cachedRawRecallsRef.current = firstRows;
              fullDataCompleteRef.current = false;
              paint();
            }
          }
        });

        if (cancelled) {
          return;
        }

        const buildQueryTime = performance.now() - buildStartTime;
        if (DEBUG_CACHE) {
          console.log(
            `[useViewportMapDataPaginated] Loaded ${aggregatedRows.length} recalls from database (${dbReadCount} DB reads, ${buildQueryTime.toFixed(0)}ms, complete=${reachedEnd})`
          );
        }

        setFullLoadError(null);

        // Only update if we got results (prevent overwriting with empty data)
        if (aggregatedRows.length > 0) {
          fullDataCompleteRef.current = reachedEnd;
          cachedRawRecallsRef.current = aggregatedRows;
          // Show the server's answer first, then persist it.
          paint();

          const decision = decideRawCacheWrite(
            aggregatedRows.length,
            reachedEnd,
            stored ? { count: stored.length, meta: cachedMeta } : null
          );
          if (decision.writeRows) {
            const cached = await setStructuredCache(CACHE_KEY_RAW_RECALLS, aggregatedRows, {
              tenantId,
              ttl: RAW_RECALLS_TTL,
              debug: DEBUG_CACHE
            });
            if (DEBUG_CACHE && !cached) {
              console.warn('[useViewportMapDataPaginated] Failed to cache recalls');
            }
          } else {
            /* eslint-disable-next-line no-console */
            console.warn(
              `[useViewportMapDataPaginated] Not caching ${aggregatedRows.length} recalls ` +
                `(complete=${reachedEnd}, stored=${stored?.length ?? 0})`
            );
          }
          if (decision.meta) {
            await setCache(CACHE_KEY_RAW_RECALLS_META, decision.meta, {
              tenantId,
              ttl: RAW_RECALLS_TTL,
              debug: DEBUG_CACHE
            });
          }
        } else {
          if (DEBUG_CACHE) {
            console.warn(
              '[useViewportMapDataPaginated] Database returned 0 recalls, keeping existing cache'
            );
          }
          // Don't overwrite existing cache with empty results
        }
      } catch (err) {
        if (!cancelled) {
          fullDataCompleteRef.current = false;
          setFullLoadError(formatViewportError(err));
          /* eslint-disable-next-line no-console */
          console.error(
            '[useViewportMapDataPaginated] Failed to load the full recall dataset',
            err
          );
        }
      } finally {
        // A cancelled load leaves these to the load that replaced it (cleanup resets them).
        if (!cancelled) {
          fullLoadInFlightRef.current = false;
          setIsCacheBuilding(false);
          setIsBackgroundLoading(false);
          // Viewport requests that waited on this load stop showing a spinner.
          setLoading(false);
        }
      }
    };

    buildFullDataset();

    return () => {
      cancelled = true;
      fullLoadInFlightRef.current = false;
      setIsCacheBuilding(false);
      setIsBackgroundLoading(false);
    };
  }, [tenantId, fullLoadNonce]);

  const refetchFullDataset = useCallback(() => {
    setFullLoadError(null);
    setError(null);
    setFullLoadNonce(n => n + 1);
  }, []);

  const runViewportQuery = useCallback(
    async (bounds: MapBounds) => {
      if (!preferencesLoaded || !tenantId) return;

      // If we have cached data, use it instead of querying database
      if (allRecallsCache && allRecallsCache.length > 0) {
        if (DEBUG_CACHE) {
          console.log(
            '[useViewportMapDataPaginated] runViewportQuery: Using cached data, skipping DB query'
          );
        }
        // Apply cached data to viewport
        if (applyCacheForBoundsRef.current) {
          applyCacheForBoundsRef.current(bounds);
        }
        return;
      }

      // Also check if we have raw cached data
      if (cachedRawRecallsRef.current && cachedRawRecallsRef.current.length > 0) {
        if (DEBUG_CACHE) {
          console.log(
            '[useViewportMapDataPaginated] runViewportQuery: Using raw cached data, skipping DB query'
          );
        }
        // Rebuild cache and apply
        rebuildCacheFromRaw();
        if (applyCacheForBoundsRef.current) {
          applyCacheForBoundsRef.current(bounds);
        }
        return;
      }

      try {
        // Optimistic UI: Never set loading to true if we have any data
        // This prevents the loading screen from showing
        if (!hasRecallsRef.current && (!recalls || recalls.length === 0) && !hasCachedData) {
          setLoading(true);
        } else {
          // We have data (either current or cached), don't show loading state
          setLoading(false);
        }
        setError(null);
        setIsLoadingMore(false);

        // STEP 2: Check full dataset cache and filter by viewport
        if (cachedRawRecallsRef.current && cachedRawRecallsRef.current.length > 0) {
          if (DEBUG_CACHE) {
            console.log(
              '[useViewportMapDataPaginated] ✅ Using full dataset cache for viewport (0 DB reads)'
            );
          }

          const viewportFilteredData = filterRecallsByBounds(
            cachedRawRecallsRef.current,
            bounds
          ) as Record<string, unknown>[];

          const transformedData: RecallVehicle[] = transformCarsWithSafetyRecallsToAppFormat(
            viewportFilteredData,
            campaignPreferences
          );

          // Deduplicate by VIN+campaign to prevent cross-zone duplicates
          const deduplicatedData = deduplicateByVinCampaign(transformedData);
          const filteredData = applyClientFilters(deduplicatedData);

          if (filteredData.length > 0) {
            hasRecallsRef.current = true;
            setHasCachedData(true);
          }
          setRecalls(filteredData);
          setHasMore(!fullDataCompleteRef.current);
          setError(null);
          setLoading(false);
          setIsLoadingMore(false);
          return;
        }
      } catch (cacheError) {
        if (DEBUG_CACHE) {
          console.warn(
            '[useViewportMapDataPaginated] Cache error, falling back to database:',
            cacheError
          );
        }
        // Continue to database query
      }

      // STEP 3: Final cache check before database query (cache might have loaded while we were in try block)
      // This prevents race conditions where cache loads after we start the function
      if (allRecallsCache && allRecallsCache.length > 0) {
        if (DEBUG_CACHE) {
          console.log(
            `[useViewportMapDataPaginated] Cache became available during execution (${allRecallsCache.length} recalls), skipping DB query`
          );
        }
        if (applyCacheForBoundsRef.current) {
          applyCacheForBoundsRef.current(bounds);
        }
        return;
      }

      if (cachedRawRecallsRef.current && cachedRawRecallsRef.current.length > 0) {
        if (DEBUG_CACHE) {
          console.log(
            `[useViewportMapDataPaginated] Raw cache became available during execution (${cachedRawRecallsRef.current.length} raw), skipping DB query`
          );
        }
        rebuildCacheFromRaw();
        if (applyCacheForBoundsRef.current) {
          applyCacheForBoundsRef.current(bounds);
        }
        return;
      }

      // STEP 4: Fallback to database query if no cache available
      if (DEBUG_CACHE) {
        console.log('[useViewportMapDataPaginated] No cache available, querying database');
      }

      try {
        // Optimistic UI: Never set loading to true if we have any data
        // This prevents the loading screen from showing
        if (!hasRecallsRef.current && (!recalls || recalls.length === 0) && !hasCachedData) {
          setLoading(true);
        } else {
          // We have data (either current or cached), don't show loading state
          setLoading(false);
        }
        setError(null);
        setIsLoadingMore(false);

        const prodClient = await getProdClientWithSession();
        const pageBudget = determinePageBudget(bounds);
        const allViewportData: Record<string, unknown>[] = [];
        let exhaustedBudgetWithData = false;
        let dbReadCount = 0;
        let offset = 0;
        const viewportStartTime = performance.now();

        for (let pageIndex = 0; pageIndex < pageBudget; pageIndex += 1) {
          if (pageIndex === 1) {
            setIsLoadingMore(true);
          }

          // Note: get_recalls_for_tenant_with_filters RPC doesn't exist
          // Use getRecallsForTenant instead and filter by bounds client-side
          const { data, error } = await getRecallsForTenant(
            prodClient,
            tenantId,
            PAGE_SIZE,
            offset
          );
          dbReadCount++;

          if (error) {
            throw new Error(`Failed to load viewport data: ${error.message || 'Unknown error'}`);
          }

          const rows = data || [];
          allViewportData.push(...rows);
          offset += rows.length;

          if (isLastPage(rows.length, PAGE_SIZE)) {
            exhaustedBudgetWithData = false;
            break;
          }

          exhaustedBudgetWithData = pageIndex === pageBudget - 1;
        }

        const viewportQueryTime = performance.now() - viewportStartTime;
        if (DEBUG_CACHE) {
          console.log(
            `[useViewportMapDataPaginated] Viewport query completed (${dbReadCount} DB reads, ${viewportQueryTime.toFixed(0)}ms)`
          );
        }

        // Filter by viewport bounds before transformation (works with raw Record data)
        const viewportFilteredData = filterRecallsByBounds(allViewportData, bounds) as Record<
          string,
          unknown
        >[];

        const transformedData: RecallVehicle[] = transformCarsWithSafetyRecallsToAppFormat(
          viewportFilteredData,
          campaignPreferences
        );

        // Deduplicate by VIN+campaign to prevent cross-zone duplicates
        const deduplicatedData = deduplicateByVinCampaign(transformedData);
        const filteredData = applyClientFilters(deduplicatedData);

        if (filteredData.length > 0) {
          hasRecallsRef.current = true;
          setHasCachedData(true);
        }
        setRecalls(filteredData);
        setHasMore(exhaustedBudgetWithData);
      } catch (err) {
        setError(formatViewportError(err));
      } finally {
        setLoading(false);
        setIsLoadingMore(false);
      }
    },
    [applyClientFilters, campaignPreferences, preferencesLoaded, tenantId]
  );

  const applyCacheForBounds = useCallback(
    (bounds: MapBounds) => {
      if (!allRecallsCache) {
        return false;
      }

      const filteredByBounds = filterRecallsByBounds(allRecallsCache, bounds) as RecallVehicle[];
      if (filteredByBounds.length > 0) {
        hasRecallsRef.current = true;
      }
      setRecalls(filteredByBounds);
      setHasMore(!fullDataCompleteRef.current);
      setError(null);
      setLoading(false);
      setIsLoadingMore(false);
      return true;
    },
    [allRecallsCache]
  );

  // Update ref when function changes
  useEffect(() => {
    applyCacheForBoundsRef.current = applyCacheForBounds;
  }, [applyCacheForBounds]);

  const loadViewportData = useCallback(
    async (bounds: MapBounds) => {
      if (!preferencesLoaded || !tenantId) return;

      lastRequestedBoundsRef.current = bounds;

      // Try to apply cached data first
      if (applyCacheForBounds(bounds)) {
        if (DEBUG_CACHE) {
          console.log(
            '[useViewportMapDataPaginated] ✅ Applied cached data to viewport, skipping DB query'
          );
        }
        return;
      }

      // Only query database if we don't have cached data
      // This prevents overwriting cached data with database queries
      if (allRecallsCache && allRecallsCache.length > 0) {
        // We have cached data - don't query database, just apply it
        if (DEBUG_CACHE) {
          console.log(
            `[useViewportMapDataPaginated] Cached data available (${allRecallsCache.length} recalls), skipping DB query`
          );
        }
        // Force apply even if it failed before (might be timing issue)
        applyCacheForBounds(bounds);
        return;
      }

      // Check if we have raw cached data that can be processed
      if (cachedRawRecallsRef.current && cachedRawRecallsRef.current.length > 0) {
        if (DEBUG_CACHE) {
          console.log(
            `[useViewportMapDataPaginated] Raw cached data available (${cachedRawRecallsRef.current.length} raw), rebuilding cache instead of querying`
          );
        }
        // Rebuild cache from raw data and apply
        rebuildCacheFromRaw();
        // Wait a tick for cache to rebuild, then apply
        setTimeout(() => {
          if (applyCacheForBoundsRef.current && lastRequestedBoundsRef.current) {
            applyCacheForBoundsRef.current(lastRequestedBoundsRef.current);
          }
        }, 0);
        return;
      }

      // Check IndexedDB cache directly (in case cachedRawRecallsRef hasn't been set yet)
      // This prevents race conditions where buildFullDataset is still loading cache
      const rawCacheFromStorage = await getStructuredCache<Record<string, unknown>[]>(
        CACHE_KEY_RAW_RECALLS,
        { tenantId, debug: DEBUG_CACHE }
      );

      if (rawCacheFromStorage && rawCacheFromStorage.length > 0) {
        if (DEBUG_CACHE) {
          console.log(
            `[useViewportMapDataPaginated] Found raw cache in storage (${rawCacheFromStorage.length} raw), rebuilding cache instead of querying`
          );
        }
        // Set the ref so buildFullDataset can use it
        cachedRawRecallsRef.current = rawCacheFromStorage;
        // Rebuild cache from raw data and apply
        rebuildCacheFromRaw();
        // Wait a tick for cache to rebuild, then apply
        setTimeout(() => {
          if (applyCacheForBoundsRef.current && lastRequestedBoundsRef.current) {
            applyCacheForBoundsRef.current(lastRequestedBoundsRef.current);
          }
        }, 0);
        return;
      }

      // The full dataset is on its way and applies lastRequestedBoundsRef when it lands;
      // a separate viewport query would only fetch the same pages twice.
      if (fullLoadInFlightRef.current) {
        if (!hasRecallsRef.current) {
          setLoading(true);
        }
        return;
      }

      // No cached data - proceed with database query
      if (DEBUG_CACHE) {
        console.log('[useViewportMapDataPaginated] No cached data available, querying database');
      }

      pendingBoundsRef.current = bounds;
      if (viewportLoadInProgressRef.current) {
        return;
      }

      while (pendingBoundsRef.current) {
        const nextBounds = pendingBoundsRef.current;
        pendingBoundsRef.current = null;
        viewportLoadInProgressRef.current = true;
        try {
          if (nextBounds) {
            await runViewportQuery(nextBounds);
          }
        } finally {
          viewportLoadInProgressRef.current = false;
        }
      }
    },
    [applyCacheForBounds, allRecallsCache, preferencesLoaded, runViewportQuery, tenantId]
  );

  // Apply cached data to viewport when cache becomes available
  useEffect(() => {
    if (allRecallsCache && allRecallsCache.length > 0 && lastRequestedBoundsRef.current) {
      if (DEBUG_CACHE) {
        console.log(
          `[useViewportMapDataPaginated] Applying cached data to viewport (${allRecallsCache.length} recalls)`
        );
      }
      // Use setTimeout to ensure this happens after any state updates
      const timeoutId = setTimeout(() => {
        const bounds = lastRequestedBoundsRef.current;
        if (applyCacheForBoundsRef.current && bounds) {
          applyCacheForBoundsRef.current(bounds);
        }
      }, 0);

      return () => clearTimeout(timeoutId);
    }
    // Add empty return for the else case
    return undefined;
  }, [allRecallsCache]);

  return {
    recalls,
    allRecallsCache,
    loading,
    error,
    loadViewportData,
    totalCount,
    isLoadingMore,
    hasMore,
    preferencesLoaded,
    isCacheBuilding,
    hasCachedData,
    isBackgroundLoading,
    fullLoadError,
    refetchFullDataset
  };
};
